<template>
	<Modal
		width="800px"
	>
		<template #modal-title>
			{{ isEditMode ? 'Edit Encryption Key' : 'Create Encryption Key' }}
		</template>

		<template #modal-title-action>
			<IconButton @click="handleClose">
				mdi-close
			</IconButton>
		</template>

		<template #modal-content>
			<div class="text-subtitle-1 text--secondary mb-4">
				Set up your encryption settings
			</div>

			<!-- Edit mode warning -->
			<v-alert
				v-if="isEditMode"
				type="info"
				dense
				outlined
				class="mb-4"
			>
				<strong>
					Editing existing key:
				</strong>
				For security, existing credentials cannot be viewed.
				Enter new credentials to update, or leave empty to keep existing.
			</v-alert>

			<!-- Loading overlay for edit mode -->
			<v-overlay
				:value="isLoadingMetadata"
				absolute
			>
				<v-progress-circular indeterminate />
			</v-overlay>

			<Tabs
				:tab.sync="activeTab"
				:tabs="tabs"
			/>

			<TabItems
				:tab="activeTab"
				:tabs="tabs"
			>
				<template #0>
					<div class="pt-4">
						<!-- Friendly Name -->
						<TextField
							v-model="form.friendlyName"
							label="Encryption Key Name *"
							placeholder="Give this key a memorable name"
							:rules="{ required: true }"
						/>

						<!-- Type Selector -->
						<encryption-type-selector
							v-model="form.selectedType"
							:types="encryptionTypes"
							class="mb-4"
						/>

						<!-- Credential Forms -->
						<native-credentials-form
							v-if="form.selectedType === 1"
							:value="form.nativeKey"
							@input="form.nativeKey = $event"
							@generate="generateAESKey"
						/>

						<salesforce-credentials-form
							v-else
							:secret="form.salesforceSecret"
							:salt="form.salesforceSalt"
							:iv="form.salesforceIV"
							@update:secret="form.salesforceSecret = $event"
							@update:salt="form.salesforceSalt = $event"
							@update:iv="form.salesforceIV = $event"
						/>

						<!-- Security Warning -->
						<v-alert
							type="warning"
							dense
							outlined
							class="mt-4"
						>
							For security, these values cannot be viewed after creation
						</v-alert>
					</div>
				</template>

				<template #1>
					<encryption-test-panel
						ref="testPanel"
						:encryption-type-id="form.selectedType"
						:credentials="currentCredentials"
						:disabled="!isFormValid"
					/>
				</template>
			</TabItems>
		</template>

		<template #modal-footer>
			<SecondaryActionButton @click="handleClose">
				Cancel
			</SecondaryActionButton>
			<PrimaryActionButton
				class="ml-2"
				:disabled="!isFormValid"
				:loading="isSaving"
				@click="handleSave"
			>
				{{ isEditMode ? 'Save' : 'Create' }}
			</PrimaryActionButton>
		</template>
	</Modal>
</template>

<script>
import { generateBase64Key, isCryptoAvailable } from '../../../../../shared/utils/encryption-utils.js'
import { getEncryptionTypes, getEncryptionKey, upsertEncryptionKey } from '../../../../../shared/utils/api/encryption-keys.js'
import { showSnackbar } from '../../../../../shared/state/snackbar.js'
import EncryptionTestPanel from './encryption-test-panel.vue'
import EncryptionTypeSelector from './encryption-type-selector.vue'
import IconButton from '../../../../../shared/components/icon-button.vue'
import Modal from '../../../../../shared/components/modal.vue'
import NativeCredentialsForm from './native-credentials-form.vue'
import PrimaryActionButton from '../../../../../shared/components/primary-action-button.vue'
import SalesforceCredentialsForm from './salesforce-credentials-form.vue'
import SecondaryActionButton from '../../../../../shared/components/secondary-action-button.vue'
import TabItems from '../../../../../shared/components/tab-items.vue'
import Tabs from '../../../../../shared/components/tabs.vue'
import TextField from '../../../../../shared/components/text-field.vue'

export default {
	name: 'encryption-config-modal',

	components: {
		EncryptionTestPanel,
		EncryptionTypeSelector,
		IconButton,
		Modal,
		NativeCredentialsForm,
		PrimaryActionButton,
		SalesforceCredentialsForm,
		SecondaryActionButton,
		TabItems,
		Tabs,
		TextField
	},

	props: {
		editKeyId: {
			type: Number,
			default: null
		}
	},

	data: function () {
		return {
			activeTab: '0',
			isSaving: false,
			isLoadingMetadata: false,
			encryptionTypes: [],
			existingKeyMetadata: null,
			form: {
				friendlyName: '',
				selectedType: 1,
				nativeKey: '',
				salesforceSecret: '',
				salesforceSalt: '',
				salesforceIV: ''
			},
			rules: {
				required: function (v) {
					return !!v || 'This field is required'
				}
			}
		}
	},

	computed: {
		isEditMode: function () {
			return this.editKeyId !== null
		},

		isFormValid: function () {
			const self = this
			if (!self.form.friendlyName) {
				return false
			}

			// For new keys, credentials are required
			if (!self.isEditMode) {
				if (self.form.selectedType === 1) {
					return self.form.nativeKey.length > 0
				} else {
					return (
						self.form.salesforceSecret.length > 0 &&
						self.form.salesforceSalt.length > 0 &&
						self.form.salesforceIV.length > 0
					)
				}
			}

			// For edit mode, credentials are optional
			// But if any Salesforce field is filled, all must be filled
			if (self.form.selectedType === 2) {
				const hasAnyCredential =
					self.form.salesforceSecret.length > 0 ||
					self.form.salesforceSalt.length > 0 ||
					self.form.salesforceIV.length > 0

				if (hasAnyCredential) {
					return (
						self.form.salesforceSecret.length > 0 &&
						self.form.salesforceSalt.length > 0 &&
						self.form.salesforceIV.length > 0
					)
				}
			}

			return true
		},

		currentCredentials: function () {
			return {
				encryptionKey: this.form.nativeKey,
				secret: this.form.salesforceSecret,
				salt: this.form.salesforceSalt,
				iv: this.form.salesforceIV
			}
		},

		tabs: function () {
			return [
				{
					title: 'Encryption Settings',
					slot: '0'
				},
				{
					title: 'Test Encryption',
					slot: '1',
					disabled: !this.isFormValid
				}
			]
		}
	},

	watch: {
		value: function (newVal) {
			if (newVal) {
				this.loadEncryptionTypes()
				if (this.editKeyId !== null) {
					this.loadExistingKey()
				}
			}
		},

		'form.selectedType': function () {
			// Reset credentials when switching types (only if not loading)
			if (!this.isLoadingMetadata) {
				this.form.nativeKey = ''
				this.form.salesforceSecret = ''
				this.form.salesforceSalt = ''
				this.form.salesforceIV = ''
			}
		}
	},

	methods: {
		handleDialogInput: function (val) {
			if (!val) {
				this.handleClose()
			}
		},

		loadEncryptionTypes: function () {
			const self = this
			getEncryptionTypes()
				.then(function (types) {
					self.encryptionTypes = types
				})
				.catch(function (error) {
					console.error('Failed to load encryption types:', error)
				})
		},

		loadExistingKey: function () {
			const self = this
			self.isLoadingMetadata = true
			getEncryptionKey(self.editKeyId)
				.then(function (metadata) {
					self.existingKeyMetadata = metadata
					self.form.friendlyName = metadata.keyName
					self.form.selectedType = metadata.encryptionTypeId
					self.isLoadingMetadata = false
				})
				.catch(function (error) {
					console.error('Failed to load encryption key:', error)
					showSnackbar({ color: 'red', text: 'Failed to load encryption key' })
					self.isLoadingMetadata = false
					self.handleClose()
				})
		},

		generateAESKey: function () {
			if (!isCryptoAvailable()) {
				showSnackbar({ color: 'red', text: 'Key generation not supported in this browser' })
				return
			}
			this.form.nativeKey = generateBase64Key(32)
		},

		resetForm: function () {
			this.form = {
				friendlyName: '',
				selectedType: 1,
				nativeKey: '',
				salesforceSecret: '',
				salesforceSalt: '',
				salesforceIV: ''
			}
			this.activeTab = '0'
			this.existingKeyMetadata = null

			// Reset test panel if it exists
			if (this.$refs.testPanel) {
				this.$refs.testPanel.reset()
			}
		},

		handleClose: function () {
			this.resetForm()
			this.$emit('close')
		},

		handleSave: function () {
			const self = this
			if (!self.isFormValid) {
				return
			}

			self.isSaving = true

			const request = {
				encryptionKeyId: self.editKeyId,
				encryptionKeyName: self.form.friendlyName,
				encryptionTypeId: self.form.selectedType
			}

			// Add credentials based on type
			if (self.form.selectedType === 1) {
				if (self.form.nativeKey) {
					request.encryptionKey = self.form.nativeKey
				}
			} else {
				if (self.form.salesforceSecret) {
					request.secret = self.form.salesforceSecret
				}
				if (self.form.salesforceSalt) {
					request.salt = self.form.salesforceSalt
				}
				if (self.form.salesforceIV) {
					request.iv = self.form.salesforceIV
				}
			}

			upsertEncryptionKey(request)
				.then(function (result) {
					if (result.errorMessage) {
						showSnackbar({ color: 'red', text: result.errorMessage })
						return
					}

					// Success feedback
					if (result.newEncryptionKeyId) {
						showSnackbar('Encryption key created successfully')
					} else {
						showSnackbar('Encryption key updated successfully')
					}

					self.$emit('saved', result)
					self.handleClose()
				})
				.catch(function (error) {
					let errorMsg = 'An unexpected error occurred'
					if (error.response && error.response.data && error.response.data.errorMessage) {
						errorMsg = error.response.data.errorMessage
					}
					showSnackbar({ color: 'red', text: errorMsg })
				})
				.finally(function () {
					self.isSaving = false
				})
		}
	}
}
</script>
